import { useState, useEffect } from 'react';
import { Bell, Smartphone, Check, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  hasActivePushSubscription,
  sendTestPushNotification,
} from '@/lib/pushNotifications';

export function PushNotificationSettings() {
  const { toast } = useToast();
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check support and subscription status on mount
  useEffect(() => {
    const checkStatus = async () => {
      setIsSupported(isPushSupported());
      setPermission(getNotificationPermission());
      
      if (isPushSupported()) {
        const subscribed = await hasActivePushSubscription();
        setIsSubscribed(subscribed);
      }
    };

    checkStatus();
  }, []);

  const handleSubscribe = async () => {
    setIsLoading(true);
    try {
      const subscription = await subscribeToPushNotifications();
      
      if (subscription) {
        setIsSubscribed(true);
        setPermission('granted');
        
        toast({
          title: 'Push notifications enabled',
          description: 'You\'ll receive notifications for important updates.',
        });
      } else {
        toast({
          title: 'Permission denied',
          description: 'Please allow notifications to receive updates.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Subscribe error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to enable push notifications',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setIsLoading(true);
    try {
      await unsubscribeFromPushNotifications();
      setIsSubscribed(false);
      
      toast({
        title: 'Push notifications disabled',
        description: 'You will no longer receive push notifications.',
      });
    } catch (error) {
      console.error('Unsubscribe error:', error);
      toast({
        title: 'Error',
        description: 'Failed to disable push notifications',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestNotification = async () => {
    setIsLoading(true);
    try {
      await sendTestPushNotification();
      
      toast({
        title: 'Test notification sent',
        description: 'Check your device for the notification.',
      });
    } catch (error) {
      console.error('Test notification error:', error);
      toast({
        title: 'Error',
        description: 'Failed to send test notification',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Push Notifications
          </CardTitle>
          <CardDescription>
            Get instant notifications on your device
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Push notifications are not supported on this device or browser.
              Please use a modern browser like Chrome, Firefox, or Safari.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Push Notifications
        </CardTitle>
        <CardDescription>
          Receive notifications even when the app is closed
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status indicator */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            <Smartphone className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="font-medium">
                {isSubscribed ? 'Notifications Enabled' : 'Notifications Disabled'}
              </p>
              <p className="text-sm text-muted-foreground">
                {isSubscribed
                  ? 'You\'ll receive push notifications on this device'
                  : 'Enable to receive push notifications'}
              </p>
            </div>
          </div>
          <div>
            {isSubscribed ? (
              <Check className="h-5 w-5 text-green-500" />
            ) : (
              <X className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Permission status */}
        {permission === 'denied' && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Notification permission was denied. Please enable notifications in your browser settings
              to receive push notifications.
            </AlertDescription>
          </Alert>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          {!isSubscribed ? (
            <Button
              onClick={handleSubscribe}
              disabled={isLoading || permission === 'denied'}
              className="flex-1"
            >
              <Bell className="h-4 w-4 mr-2" />
              Enable Push Notifications
            </Button>
          ) : (
            <>
              <Button
                onClick={handleTestNotification}
                disabled={isLoading}
                variant="outline"
                className="flex-1"
              >
                Send Test Notification
              </Button>
              <Button
                onClick={handleUnsubscribe}
                disabled={isLoading}
                variant="outline"
              >
                Disable
              </Button>
            </>
          )}
        </div>

        {/* Info */}
        <div className="text-sm text-muted-foreground space-y-2">
          <p className="font-medium">You'll receive notifications for:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>New emails in your inbox</li>
            <li>Task due date reminders</li>
            <li>Opportunity updates</li>
            <li>@mentions in notes and comments</li>
            <li>Document expiration alerts</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
